import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Languages, Moon, Sun, Timer as TimerIcon, AlarmClock, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { TimerDialog } from "@/components/todo/TimerDialog";
import { AlarmDialog } from "@/components/todo/AlarmDialog";
import { ProfileMenu } from "@/components/todo/ProfileMenu";
import { pushSupported, isPushSubscribed, enablePush } from "@/lib/push";

export const Header = ({ tasks = [] }) => {
  const { t, lang, theme, toggleLang, toggleTheme } = useApp();
  const [timerOpen, setTimerOpen] = useState(false);
  const [alarmOpen, setAlarmOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    isPushSubscribed().then(setPushOn);
  }, []);

  const handlePush = async () => {
    if (!pushSupported()) {
      toast.error(t("pushUnsupported"));
      return;
    }
    try {
      await enablePush();
      setPushOn(true);
      toast.success(t("pushSuccess"));
    } catch (e) {
      toast.error(e.message === "denied" ? t("pushDenied") : t("pushUnsupported"));
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="app-logo">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm shrink-0">
            <CheckCircle2 className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="leading-tight min-w-0">
            <h1 className="font-heading font-bold text-base sm:text-lg tracking-tight truncate">{t("appName")}</h1>
            <p className="text-xs text-muted-foreground -mt-0.5 truncate">{t("appTagline")}</p>
          </div>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
          <button
            data-testid="push-toggle-btn"
            onClick={handlePush}
            title={pushOn ? t("pushEnabled") : t("enablePush")}
            className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg transition-colors ${
              pushOn ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
            aria-label={t("enablePush")}
          >
            {pushOn ? <BellRing className="w-[18px] h-[18px]" /> : <Bell className="w-[18px] h-[18px]" />}
          </button>
          <button
            data-testid="open-timer-btn"
            onClick={() => setTimerOpen(true)}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label={t("timer")}
          >
            <TimerIcon className="w-[18px] h-[18px]" />
          </button>
          <button
            data-testid="open-alarm-btn"
            onClick={() => setAlarmOpen(true)}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label={t("alarmTitle")}
          >
            <AlarmClock className="w-[18px] h-[18px]" />
          </button>
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="flex items-center gap-1 sm:gap-1.5 h-8 px-2 sm:h-9 sm:px-2.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Sprache wechseln"
          >
            <Languages className="w-4 h-4" />
            <span className="uppercase">{lang}</span>
          </button>
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Design wechseln"
          >
            <motion.span
              key={theme}
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {theme === "light" ? (
                <Moon className="w-[18px] h-[18px]" />
              ) : (
                <Sun className="w-[18px] h-[18px]" />
              )}
            </motion.span>
          </button>
          <ProfileMenu />
        </div>
      </div>

      <TimerDialog open={timerOpen} onOpenChange={setTimerOpen} tasks={tasks} />
      <AlarmDialog open={alarmOpen} onOpenChange={setAlarmOpen} />
    </header>
  );
};
